import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useWeb3 } from './Web3Context';
import api from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { web3, account, isConnected } = useWeb3();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);
  const [isLoading, setIsLoading] = useState(false);
  const authAttemptedRef = useRef(false);

  // Request nonce for wallet signature
  const requestNonce = useCallback(async (walletAddress) => {
    try {
      const response = await api.post('/auth/nonce', { walletAddress });
      return response.data.message;
    } catch (error) {
      console.error('Nonce request error:', error);
      throw error;
    }
  }, []);

  // Sign message and login
  const login = useCallback(async () => {
    if (!web3 || !account) {
      throw new Error('Wallet not connected');
    }

    try {
      setIsLoading(true);
      
      // Request nonce
      const message = await requestNonce(account);

      // Sign message
      const signature = await web3.eth.personal.sign(message, account, '');

      // Send signature to backend
      const response = await api.post('/auth/login', {
        walletAddress: account,
        signature
      });

      const { token, user: userData } = response.data;
      
      setToken(token);
      setUser(userData);
      setIsAuthenticated(true);
      localStorage.setItem('token', token);

      return { token, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [web3, account, requestNonce]);

  // Logout
  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('token');
  }, []);

  // Verify token and get user
  const verifyAuth = useCallback(async () => {
    if (!token) {
      setIsAuthenticated(false);
      return;
    }

    try {
      const response = await api.get('/auth/verify');
      if (response.data.valid) {
        setUser(response.data.user);
        setIsAuthenticated(true);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Auth verification error:', error);
      logout();
    }
  }, [token, logout]);

  // When the connected wallet changes, clear any stale session from a different wallet.
  // This prevents showing another user's data after switching accounts in MetaMask.
  useEffect(() => {
    if (isConnected && account && user?.walletAddress) {
      if (account.toLowerCase() !== user.walletAddress.toLowerCase()) {
        logout();
      }
    }
  }, [isConnected, account, user, logout]);

  // Reset auth attempt flag when the wallet account changes
  useEffect(() => { authAttemptedRef.current = false; }, [account]);

  // Auto sign-in when wallet is connected: verify existing token first,
  // then seamlessly trigger wallet signature if no valid token exists.
  useEffect(() => {
    if (!isConnected || !account || isAuthenticated || isLoading) return;
    if (authAttemptedRef.current) return; // don't retry after a failed attempt
    authAttemptedRef.current = true;

    const autoSignIn = async () => {
      // 1. If a stored token exists, try to re-use it
      const stored = localStorage.getItem('token');
      if (stored) {
        try {
          const res = await api.get('/auth/verify');
          if (res.data.valid) {
            setToken(stored);
            setUser(res.data.user);
            setIsAuthenticated(true);
            return;
          }
        } catch (_) {
          // Token expired/invalid — fall through to re-sign
          localStorage.removeItem('token');
        }
      }
      // 2. No valid token — silently trigger wallet signature
      try {
        await login();
      } catch (_) {
        // User cancelled signature or sign failed — leave unauthenticated for now
      }
    };

    autoSignIn();
  }, [isConnected, account, isAuthenticated]); // eslint-disable-line

  // Verify auth on mount
  useEffect(() => {
    if (token) {
      verifyAuth();
    }
  }, [token, verifyAuth]);

  // (Legacy) cross-domain SSO is no longer needed because Store lives on the same domain (/store).

  const value = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    verifyAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
